import { AxiosRequestConfig } from 'axios';
import axios from '../axios-base';
import { BlazeMeterApiResponse } from 'types/rest/blazeMeterApiResponse';
import { AssetType } from 'store/certificates/certificatesTypes';
import { Asset, BulkOperation, Certificate, RecordingMessage } from '../../types/rest/assetCatalogTypes';
import { Configuration } from '../../types/rest/configurationsType';
import { cacheControlHeaders } from '../../utils/miscUtil';
import { MockServiceGroup } from '../../store/mockServiceGroups/mockServiceGroupTypes';
import { LocationPort } from '../../views/MockServices/Tabs/MockServices/Cells/MockPreferredPortCell';
import { UsageReportRequest } from '../../views/Admin/UsageReport/types';
import { RoiSummaryRequest } from '../../views/Admin/RoiInsights/types';
import { AuditLogQueryParams } from '../../views/Admin/AuditLog/types';
import { MockServiceApiParams, workspaceBaseApi } from './apiCommon';
import { Base64 } from 'js-base64';
import { AccountSettingsPatchDto } from '../../types/rest/accountSettingsTypes';

export const serviceMocksApi = `${workspaceBaseApi}/service-mocks`;

export const groupsApi = `${workspaceBaseApi}/service-mock-groups`;

export default {
  userDetails: {
    getUser: () => {
      return axios.get('/user');
    },
    patchPreferences: (payload: any) => {
      return axios.patch('/user/preferences', payload);
    },
    refreshActiveAccount: () => {
      return axios.post('/user/active-account');
    },
  },
  recordings: {
    getRecording: (recordingId: number) => {
      return axios.get(`${workspaceBaseApi}/recordings/${recordingId},`);
    },
    patchRecording: (recordingId: number, recording: any) => {
      return axios.patch(`${workspaceBaseApi}/recordings/${recordingId}`, recording);
    },
    postRecording: (recording: any, params: any) => {
      return axios.post(`${workspaceBaseApi}/recordings/`, recording, { params: params });
    },
    convertToTransaction: (recordingId: number, params: any) => {
      return axios.post(`${workspaceBaseApi}/recordings/${recordingId}/convert`, {}, { params: params });
    },
    deleteRecording: (recordingId: number) => {
      return axios.delete(`${workspaceBaseApi}/recordings/${recordingId}`);
    },
    getRecordings: (params: any) => {
      return axios.get(`${workspaceBaseApi}/recordings`, { params: params });
    },
    import: (recordingDto: any) => {
      return axios.post(`${workspaceBaseApi}/recordings/import`, recordingDto);
    },
    convertHttpToTransaction: (recordingDto: any, params: any) => {
      return axios.post(`${workspaceBaseApi}/analytics/import`, recordingDto, { params: params });
    },
    deleteBulkRecordings: (recordingsIds: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/recordings`, {
        action: 'DELETE',
        ids: recordingsIds,
      });
    },
    tagBulkRecordings: (bulkTagAction: BulkOperation) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/recordings`, bulkTagAction);
    },
    postMessage: (recordingId: number, message: RecordingMessage) => {
      return axios.post(`${workspaceBaseApi}/recordings/${recordingId}/messages`, message);
    },
    putMessage: (message: RecordingMessage) => {
      return axios.put(`${workspaceBaseApi}/recordings/${message.recordingId}/messages/${message.id}`, message);
    },
    getMessage: (recordingId: number, messageId: number) => {
      return axios.get(`${workspaceBaseApi}/recordings/${recordingId}/messages/${messageId}`);
    },
    deleteMessage: (recordingId: number, messageId: number) => {
      return axios.delete(`${workspaceBaseApi}/recordings/${recordingId}/messages/${messageId}`);
    },
    getMessages: (recordingId: number, params: any) => {
      return axios.get(`${workspaceBaseApi}/recordings/${recordingId}/messages`, { params: params });
    },
    deleteBulkMessages: (recordingId: number, messagesIds: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/recordings/${recordingId}/messages`, {
        action: 'DELETE',
        ids: messagesIds,
      });
    },
  },
  assets: {
    getAssets: (assetType?: AssetType) => {
      return axios.get(`${workspaceBaseApi}/assets`, { params: { type: assetType } });
    },
    validateByName: (assetName: string, assetType?: AssetType, serviceId?: number) => {
      return axios.post(`${workspaceBaseApi}/assets/validate-name`, {
        name: assetName,
        serviceId: serviceId,
        assetType: assetType,
      });
    },
    validate: (assetId: number) => {
      return axios.get(`${workspaceBaseApi}/assets/${assetId}/validate`);
    },
    updateAsset: (asset: Certificate | Asset) => {
      return axios.patch(`${workspaceBaseApi}/assets/${asset.id}`, asset);
    },
    getById: (assetId?: number) => {
      return axios.get(`${workspaceBaseApi}/assets/${assetId}`);
    },
    download: (assetId: number) => {
      return axios.get(`${workspaceBaseApi}/assets/${assetId}/download`);
    },
    upload: (formData: FormData) => {
      return axios.post(`${workspaceBaseApi}/assets`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    getKeystoreAliases: (assetId: number) => {
      return axios.get(`${workspaceBaseApi}/assets/${assetId}/keystore/aliases`);
    },
    deleteBulk: (filesIds: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/assets`, {
        action: 'DELETE',
        assetIds: filesIds,
      });
    },
    delete: (fileId: number, header) => {
      return axios.delete(`${workspaceBaseApi}/assets/${fileId}`, {
        headers: header,
      });
    },
  },
  mockServices: {
    getAll: (
      params?: MockServiceApiParams & {
        shipIds?: string;
        status?: string;
        onlyWarnings?: boolean;
      },
    ) => {
      return axios.get(`${serviceMocksApi}`, { params: params });
    },
    getById: (serviceMockId: string | number) => {
      return axios.get(`${serviceMocksApi}/${serviceMockId}`);
    },
    getBlazeData: (serviceMockId: string | number) => {
      return axios.get(`${serviceMocksApi}/${serviceMockId}/testdata`);
    },
    refreshBlazeData: (serviceMockId: string | number) => {
      return axios.get(`${serviceMocksApi}/${serviceMockId}/testdata/refresh`);
    },
    deploy: (serviceMockId: string | number, config?: AxiosRequestConfig) => {
      return axios.get(`${serviceMocksApi}/${serviceMockId}/deploy`, config);
    },
    getTransactions: (mockServiceId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mocks/${mockServiceId}/transactions`, { params: params });
    },
    getRecordings: (mockServiceId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mocks/${mockServiceId}/recordings`, { params: params });
    },
    startRecording: (mockServiceId: number, params: { runnerType: 'HTTP' | 'MESSAGING' }) => {
      return axios.get(`${serviceMocksApi}/${mockServiceId}/start-recording`, { params: params });
    },
    stopRecording: (mockServiceId: number, params: { runnerType: 'HTTP' | 'MESSAGING' }) => {
      return axios.get(`${serviceMocksApi}/${mockServiceId}/stop-recording`, { params: params });
    },
    deleteRecording: (mockServiceId: number) => {
      return axios.get(`${serviceMocksApi}/${mockServiceId}/delete-recording`);
    },
    refreshLogs: (mockServiceId: number | string, numberOfLines: number) => {
      return axios.post(`${serviceMocksApi}/${mockServiceId}/log/refresh?limit=${numberOfLines}`);
    },
    getLogs: (mockServiceId: number | string, numberOfLines: number) => {
      return axios.get(`${serviceMocksApi}/${mockServiceId}/log?limit=${numberOfLines}`);
    },
    patchMockService: (mockServiceId: number, fieldsToUpdate: any) => {
      return axios.patch(`${serviceMocksApi}/${mockServiceId}`, fieldsToUpdate);
    },
    testMessagingConnection: (mockServiceId: number) => {
      return axios.get(`${serviceMocksApi}/${mockServiceId}/test-connection`);
    },
  },
  groups: {
    getById: (groupId: number) => {
      return axios.get(`${groupsApi}/${groupId}`);
    },
    getMockServices: (groupId: number) => {
      return axios.get(`${groupsApi}/${groupId}/servicemocks`);
    },
    postMockServicesGroup: (smGroup: MockServiceGroup) => {
      return axios.post(`${groupsApi}`, smGroup);
    },
    getMockServiceGroupList: (params: any) => {
      return axios.get(`${groupsApi}`, {
        headers: cacheControlHeaders,
        params: params,
      });
    },
    deleteMockServiceGroup: (groupId: number) => {
      return axios.delete(`${groupsApi}/${groupId}`, {});
    },
    bulkMockServiceGroups: (action: string, groupIds: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${groupsApi}`, {
        action,
        mockServiceIds: groupIds,
      });
    },
    putMockServiceGroup: (groupId: number, smGroup: MockServiceGroup) => {
      return axios.put(`${groupsApi}/${groupId}`, smGroup);
    },
    patchMockServiceGroup: (groupId: number, fieldsToUpdate: any) => {
      return axios.patch(`${groupsApi}/${groupId}`, fieldsToUpdate);
    },
    deploy: (groupId: number, config?: AxiosRequestConfig) => {
      return axios.get(`${groupsApi}/${groupId}/deploy`, config);
    },
    stop: (groupId: number, config?: AxiosRequestConfig) => {
      return axios.get(`${groupsApi}/${groupId}/stop`, config);
    },
    configure: (groupId: number, config?: AxiosRequestConfig) => {
      return axios.get(`${groupsApi}/${groupId}/configure`, config);
    },
  },
  transactions: {
    getTransaction: (transactionId: number | string) => {
      return axios.get(`${workspaceBaseApi}/transactions/${transactionId}`);
    },
    getTransactions: (config?: AxiosRequestConfig) => {
      return axios.get(`${workspaceBaseApi}/transactions`, config);
    },
    getArchivedTransactions: (config?: AxiosRequestConfig) => {
      return axios.get(`${workspaceBaseApi}/transactions/archived`, config);
    },
    restoreTransactions: (ids: number[]) => {
      return axios.post(`${workspaceBaseApi}/transactions/restore`, ids);
    },
    /**
     * Imports WireMock stub mappings from a live admin URL and creates
     * HTTP transactions in the given service.
     *
     * @param serviceId target service ID
     * @param body      import request body (url, authType, credentials, tags)
     */
    importFromUrl: (
      serviceId: number,
      body: {
        url: string;
        authType: 'NONE' | 'BASIC' | 'API_TOKEN';
        username?: string;
        password?: string;
        apiToken?: string;
        tags?: string[];
      },
    ) => {
      return axios.post(`${workspaceBaseApi}/services/${serviceId}/import-from-url`, body);
    },
    // MOB-47723 transaction version history
    getTransactionVersions: (transactionId: number) => {
      return axios.get(`${workspaceBaseApi}/transactions/${transactionId}/versions`);
    },
    getTransactionVersion: (transactionId: number, versionNumber: number) => {
      return axios.get(`${workspaceBaseApi}/transactions/${transactionId}/versions/${versionNumber}`);
    },
    getTransactionVersionDiff: (transactionId: number, from?: number, to?: number) => {
      return axios.get(`${workspaceBaseApi}/transactions/${transactionId}/versions/diff`, { params: { from, to } });
    },
    restoreTransactionVersion: (transactionId: number, versionNumber: number) => {
      return axios.post(`${workspaceBaseApi}/transactions/${transactionId}/versions/${versionNumber}/restore`);
    },
  },
  sandbox: {
    getSandbox: (transactionId?: number) => {
      const url = transactionId
        ? `${workspaceBaseApi}/sandbox?transactionId=${transactionId}`
        : `${workspaceBaseApi}/sandbox`;
      return axios.get(url);
    },
    updateSandbox: (sandBox: any, config?: any) => {
      return axios.patch(`${workspaceBaseApi}/sandbox`, sandBox, config);
    },
    testRequest: (sandBoxTransactionId: number, dsl?: any, httpRequest?: any) => {
      const url = `${workspaceBaseApi}/sandbox/test-request`;
      return axios.post(url, { transactionId: sandBoxTransactionId, dsl, httpRequest });
    },
    generateRequest: (dsl: object) => {
      return axios.post(`${workspaceBaseApi}/sandbox/ai/generate-request`, dsl);
    },
    getGenerationStatus: () => {
      return axios.get(`${workspaceBaseApi}/sandbox/generation-status`);
    },
    updateActionMocks: (actionMocks: any[]) => {
      return axios.put(`${workspaceBaseApi}/sandbox/actions`, actionMocks);
    },
    getDatasetState: () => {
      return axios.get(`${workspaceBaseApi}/sandbox/dataset-state`);
    },
    resetCache: () => {
      return axios.delete(`${workspaceBaseApi}/sandbox/cache`);
    },
  },
  secrets: {
    getVaultIntegrationList: () => {
      return axios.get(`${workspaceBaseApi}/vault-integrations`);
    },
  },
  configurations: {
    getConfigurations: () => {
      return axios.get(`${workspaceBaseApi}/service-mock-configurations?sort=-id`);
    },
    postConfiguration: (configuration: Configuration) => {
      return axios.post(`${workspaceBaseApi}/service-mock-configurations`, configuration);
    },
    putConfiguration: (configuration: Configuration) => {
      return axios.put(`${workspaceBaseApi}/service-mock-configurations/${configuration.id}`, configuration);
    },
    deleteConfiguration: (id: number, force: boolean) => {
      return axios.delete(`${workspaceBaseApi}/service-mock-configurations/${id}?force=${force}`);
    },
    getCountUsage: (id: number) => {
      return axios.get(`${workspaceBaseApi}/service-mock-configurations/${id}/usage-count`);
    },
    bulkDeleteConfigurations: (ids: number[]) => {
      return axios.patch(`${workspaceBaseApi}/service-mock-configurations`, { action: 'DELETE', ids });
    },
  },
  mockServiceDrafts: {
    getById: (draftId: string | number) => {
      return axios.get(`${workspaceBaseApi}/service-mock-drafts/${draftId}`);
    },
    getTransactions: (draftId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-drafts/${draftId}/transactions`, { params: params });
    },
    getRecordings: (draftId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-drafts/${draftId}/recordings`, { params: params });
    },
    updateTransaction: (templateId: number, transactionId: number, data: any) => {
      return axios.patch(`${workspaceBaseApi}/service-mock-drafts/${templateId}/transactions/${transactionId}`, data);
    },
    patchDraftById: (draftId: string | number, data: any) => {
      return axios.patch(`${workspaceBaseApi}/service-mock-drafts/${draftId}`, data);
    },
    updateRecording(mockServiceId: number, recordingId: number, data: any) {
      return axios.patch<BlazeMeterApiResponse>(
        `${workspaceBaseApi}/service-mock-drafts/${mockServiceId}/recordings/${recordingId}`,
        data,
      );
    },
  },
  templates: {
    getById: (templateId: string | number) => {
      return axios.get(`${workspaceBaseApi}/service-mock-templates/${templateId}`);
    },
    getTransactions: (templateId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-templates/${templateId}/transactions`, {
        params: params,
      });
    },
    getRecordings: (templateId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-templates/${templateId}/recordings`, { params: params });
    },
    bulkDeleteMockServiceTemplates: (ids: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/service-mock-templates`, {
        action: 'DELETE',
        mockServiceIds: ids,
      });
    },
  },
  templateDrafts: {
    getById: (templateId: string | number) => {
      return axios.get(`${workspaceBaseApi}/service-mock-template-drafts/${templateId}`);
    },
    getTransactions: (templateDraftId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-template-drafts/${templateDraftId}/transactions`, {
        params: params,
      });
    },
    updateTransaction: (templateId: number, transactionId: number, data: any) => {
      return axios.patch(
        `${workspaceBaseApi}/service-mock-template-drafts/${templateId}/transactions/${transactionId}`,
        data,
      );
    },
    getRecordings: (templateId: number, params: MockServiceApiParams) => {
      return axios.get(`${workspaceBaseApi}/service-mock-template-drafts/${templateId}/recordings`, { params: params });
    },
    patchDraftById: (draftId: string | number, data: any) => {
      return axios.patch(`${workspaceBaseApi}/service-mock-template-drafts/${draftId}`, data);
    },
    updateRecording(mockServiceId: number, recordingId: number, data: any) {
      return axios.patch<BlazeMeterApiResponse>(
        `${workspaceBaseApi}/service-mock-template-drafts/${mockServiceId}/recordings/${recordingId}`,
        data,
      );
    },
    bulkDeleteMockServiceTemplateDrafts: (ids: number[]) => {
      return axios.patch<BlazeMeterApiResponse>(`${workspaceBaseApi}/service-mock-template-drafts`, {
        action: 'DELETE',
        mockServiceIds: ids,
      });
    },
  },
  blueprints: {
    list: (params?: { keyword?: string; tags?: string[]; skip?: number; limit?: number; sort?: string }) =>
      axios.get('/blueprints', { params }),
    getById: (id: string | number, params?: { includeImage?: boolean; includeTransactions?: boolean }) =>
      axios.get(`/blueprints/${id}`, { params }),
    create: (serviceId: number, transactionIdsFilter: number[] | undefined, body: unknown) =>
      axios.post(`/blueprints/from-service/${serviceId}`, body, {
        params: transactionIdsFilter && transactionIdsFilter.length > 0 ? { transactionIdsFilter } : undefined,
      }),
    update: (id: string | number, body: unknown) => axios.put(`/blueprints/${id}/metadata`, body),
    uploadImage: (id: string | number, formData: FormData) =>
      axios.put(`/blueprints/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
    delete: (id: string | number) => axios.delete(`/blueprints/${id}`),
    apply: (
      id: string | number,
      params: { workspaceId: number; serviceId?: number; newServiceName?: string; skipBlazeData?: boolean },
    ) => axios.post(`/blueprints/${id}/apply`, null, { params }),
  },
  trackings: {
    getTrackingById: (requestId: string) => {
      return axios.get(`/trackings/${requestId}`);
    },
  },
  location: {
    postValidateLocationPort: (locPort: LocationPort) => {
      return axios.post(`${workspaceBaseApi}/locations/validate-port`, locPort);
    },
  },
  aiAssistant: {
    /**
     * Get expression suggestion from AI (supports Regex, XPath, JSONPath, Handlebars)
     */
    getExpressionSuggestion: (question: string, conversationId?: string) => {
      const q = Base64.encode(question);
      return axios.get<BlazeMeterApiResponse>(`${workspaceBaseApi}/aiassistant/query`, {
        params: { question: q, conversationId },
      });
    },
    getConversation: (conversationId: string) => {
      return axios.get<BlazeMeterApiResponse>(`${workspaceBaseApi}/aiassistant/conversations/${conversationId}`);
    },
  },
  adminReporting: {
    getUsageReport: (request: UsageReportRequest) => {
      return axios.post<BlazeMeterApiResponse>('/admin/usage-report', request);
    },
    getRoiSummary: (request: RoiSummaryRequest) => {
      return axios.post<BlazeMeterApiResponse>('/admin/roi-summary', request);
    },
  },
  accountSettings: {
    getSettings: () => {
      return axios.get<BlazeMeterApiResponse>('/accounts/{accountId}/settings');
    },
    patchSettings: (patch: AccountSettingsPatchDto) => {
      return axios.patch<BlazeMeterApiResponse>('/accounts/{accountId}/settings', patch);
    },
  },
  auditLog: {
    getAccountAuditLog: (params: AuditLogQueryParams) => {
      return axios.get<BlazeMeterApiResponse>('/accounts/{accountId}/audit-log', { params });
    },
    getWorkspaceAuditLog: (workspaceId: number, params: AuditLogQueryParams) => {
      return axios.get<BlazeMeterApiResponse>(`/workspaces/${workspaceId}/audit-log`, { params });
    },
  },
};
